import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { motion } from 'framer-motion';
import { API_BASE } from '../lib/apiBase';
import { toUrlCategorySlug } from '../lib/categoryUrlSlug';

// Rewrite `/uploads/foo.jpg` → `${API_BASE}/api/uploads/foo.jpg` so the
// request hits the backend StaticFiles mount. The bare `/uploads/*` path
// only works for assets baked into the static frontend bundle at build
// time; runtime-uploaded admin overrides live ONLY on the backend pod and
// must transit through the `/api/*` ingress proxy on the live site.
const toAbs = (url) => (
  url && url.startsWith('/uploads/')
    ? `${API_BASE}/api${url}`
    : url
);

// Default Unsplash images — used when no admin override exists in the DB.
const defaultCategoryImages = {
  tours_sightseeing: "https://images.unsplash.com/photo-1775664377425-b065aef92bde?crop=entropy&cs=srgb&fm=jpg&w=400&h=300&fit=crop&q=85",
  food_wine: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?crop=entropy&cs=srgb&fm=jpg&w=400&h=300&fit=crop&q=85",
  water_activities: "https://images.unsplash.com/photo-1518837695005-2083093ee35b?crop=entropy&cs=srgb&fm=jpg&w=400&h=300&fit=crop&q=85",
  beaches: "https://images.unsplash.com/photo-1566073771259-6a8506099945?crop=entropy&cs=srgb&fm=jpg&w=400&h=300&fit=crop&q=85",
  culture_arts: "https://images.unsplash.com/photo-1563293743-a9761195b52e?crop=entropy&cs=srgb&fm=jpg&w=400&h=300&fit=crop&q=85",
  nightlife: "https://images.unsplash.com/photo-1556163117-3c1f25d91c45?crop=entropy&cs=srgb&fm=jpg&w=400&h=300&fit=crop&q=85",
  romantic: "https://images.unsplash.com/photo-1518709268805-4e9042af9f23?crop=entropy&cs=srgb&fm=jpg&w=400&h=300&fit=crop&q=85",
  transfers: "https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?crop=entropy&cs=srgb&fm=jpg&w=400&h=300&fit=crop&q=85"
};

/**
 * Renders a category tile image. When the backend provides an `images`
 * object (admin-uploaded), we emit a <picture> with WebP @1x/@2x sources
 * and a JPEG fallback <img>. Otherwise we fall back to the hardcoded Unsplash
 * URL. Always lazy-loaded.
 */
function CategoryTileImage({ category, alt }) {
  const imgs = category.images;
  if (imgs && (imgs.avif_1x || imgs.webp_1x || imgs.jpeg_1x)) {
    // Admin overrides ship a 600px _1x and a 1200px _2x. Category tiles are at
    // most ~592px @DPR2, so the 600px _1x already covers every device — serve
    // it as the ONLY source and skip the oversized _2x.
    const a1 = toAbs(imgs.avif_1x);
    const w1 = toAbs(imgs.webp_1x);
    const j1 = toAbs(imgs.jpeg_1x);
    return (
      <picture>
        {a1 && <source type="image/avif" srcSet={a1} />}
        {w1 && <source type="image/webp" srcSet={w1} />}
        <img
          src={j1 || w1}
          alt={alt}
          width="400"
          height="300"
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
      </picture>
    );
  }
  // Default Unsplash tile: serve WebP with a real 1x(400)/2x(800) srcset —
  // 400 covers non-retina (~296px slot), 800 covers desktop retina (~592px).
  const base = defaultCategoryImages[category.slug] || defaultCategoryImages.tours_sightseeing;
  const webp1 = base.replace('fm=jpg', 'fm=webp');
  const webp2 = webp1.replace('w=400', 'w=800').replace('h=300', 'h=600');
  return (
    <picture>
      <source type="image/webp" srcSet={`${webp1} 1x, ${webp2} 2x`} />
      <img
        src={base}
        alt={alt}
        width="400"
        height="300"
        loading="lazy"
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
      />
    </picture>
  );
}

export function CategoriesSection({ categories }) {
  const { t, localized } = useLanguage();
  const navigate = useNavigate();

  const handleCategoryClick = (category) => {
    // Navigate to Explore page with category filter pre-selected
    navigate(`/Explore?category=${encodeURIComponent(toUrlCategorySlug(category.slug))}`);
  };

  return (
    <section className="py-[30px] sm:py-[40px]" data-testid="categories-section" id="categories">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6">
        <h2 className="h-font text-[#0F3B63] text-[29px] sm:text-[38px] font-bold tracking-tight">
          {t('categories_title')}
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mt-6">
          {categories.map((cat, i) => {
            const name = localized(cat, 'name');
            return (
              <motion.button
                key={cat.slug}
                data-testid={`category-${cat.slug}`}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.35, delay: i * 0.06 }}
                onClick={() => handleCategoryClick(cat)}
                className="group text-left bg-transparent transition-all cursor-pointer"
              >
                <div className="relative aspect-[4/3] overflow-hidden rounded-2xl">
                  <CategoryTileImage category={cat} alt={name} />
                </div>
                <p
                  className="mt-3 text-left text-[17px] sm:text-[19px] font-bold leading-tight"
                  style={{ color: '#0F3B63' }}
                >
                  {name}
                </p>
              </motion.button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
